// What a POS screen shows when its feature is not in the business's plan
// (docs/plan-limits-plan.md §7 — lock, never hide). The owner gets a link to
// My Subscription in the admin app; everyone else is told who can upgrade.
// Plain markup on purpose: no @bbpos/ui components, so route tests that mock
// the UI package still render it.

import { useNavigate } from 'react-router-dom';
import { PLAN_FEATURES, type PlanFeatureKey } from '@bbpos/types';
import { useAuthStore } from '@/stores/use-auth-store';
import { appUrl } from '@/lib/app-url';

export function LockIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

/** The small lock shown next to a button or nav label whose feature is locked. */
export function LockMark() {
  return (
    <span
      role="img"
      aria-label="Not in your plan"
      title="Not in your plan"
      className="inline-flex text-warning-500 dark:text-warning-400"
    >
      <LockIcon />
    </span>
  );
}

export function LockedFeature({ feature }: { feature: PlanFeatureKey }) {
  const navigate = useNavigate();
  const isOwner = useAuthStore((s) => s.profile?.role === 'OWNER');
  const { label, description } = PLAN_FEATURES.find((f) => f.key === feature)!;

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12" role="main" aria-label={`${label} is locked`}>
      <div
        className="w-full max-w-md rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50 via-white to-indigo-50 p-10 text-center shadow-md animate-fade-in dark:border-neutral-700 dark:from-brand-900/20 dark:via-neutral-900 dark:to-indigo-900/20"
        data-testid="locked-feature"
      >
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-indigo-500 text-white shadow-lg">
          <LockIcon className="h-8 w-8" />
        </div>
        <span className="mb-3 inline-flex items-center rounded-full bg-warning-100 px-3 py-1 text-xs font-semibold text-warning-700 dark:bg-warning-900/30 dark:text-warning-300">
          Locked
        </span>
        <p className="text-xl font-bold text-neutral-900 dark:text-white">{label}</p>
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{description}</p>
        <p className="mt-4 font-medium text-neutral-900 dark:text-white">This is not in your plan.</p>
        {isOwner ? (
          <a
            href={appUrl('admin', '/my-subscription')}
            className="mt-5 inline-flex items-center rounded-lg bg-gradient-to-r from-brand-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg"
          >
            Upgrade
          </a>
        ) : (
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">Ask your owner to upgrade.</p>
        )}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => navigate('/pos/billing')}
            className="text-sm font-medium text-brand-600 transition-colors duration-200 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
          >
            Go to Billing
          </button>
        </div>
      </div>
    </div>
  );
}
